import {
  Direction,
  EngagementFilterType,
  MessageContactType,
  MessagingROIType,
  TimeFilterType,
  type MessagingRequest,
  type MessagingTableViewData,
  type MessagingTableViewResponse,
  type ROIMessageAggregatesResponse,
  type ROIMessageDataPoint,
  type ROIMessageDataResponse,
  type ROIMessagingApptAggregatesResponse,
} from '@weave/schema-gen-ts/dist/schemas/analytics-reporting/analytics_gateway/analytics_gateway.pb';
import dayjs from 'dayjs';

// Only these ROI types fall back to demo data; the others (e.g. manual messages) always show real data.
const DEMO_ROI_TYPES: MessagingROIType[] = [
  MessagingROIType.ROI_TYPE_MANUAL_MESSAGES,
  MessagingROIType.ROI_TYPE_MISSED_CALL_TEXT,
  MessagingROIType.ROI_TYPE_APPOINTMENT_REMINDERS,
  MessagingROIType.ROI_TYPE_RECALL_REMINDERS,
  MessagingROIType.ROI_TYPE_REACTIVATION_TEXT,
  MessagingROIType.ROI_TYPE_RESCHEDULE_TEXT,
];

export const isMessagingRoiDemoType = (roiType: MessagingROIType) => DEMO_ROI_TYPES.includes(roiType);

interface RoiDemoProfile {
  dailyVolume: number;
  /** Percentage (0-100) of messages that got a response. */
  engagementRate: number;
  /** Appointments scheduled as a share of total messages. */
  scheduledRatio: number;
  /** Appointments completed as a share of total messages. */
  completedRatio: number;
}

const ROI_DEMO_PROFILES: Partial<Record<MessagingROIType, RoiDemoProfile>> = {
  [MessagingROIType.ROI_TYPE_MANUAL_MESSAGES]: {
    dailyVolume: 15,
    engagementRate: 40,
    scheduledRatio: 0.2,
    completedRatio: 0.15,
  },
  [MessagingROIType.ROI_TYPE_MISSED_CALL_TEXT]: {
    dailyVolume: 9,
    engagementRate: 34,
    scheduledRatio: 0.12,
    completedRatio: 0.08,
  },
  [MessagingROIType.ROI_TYPE_APPOINTMENT_REMINDERS]: {
    dailyVolume: 38,
    engagementRate: 58,
    scheduledRatio: 0.9,
    completedRatio: 0.74,
  },
  [MessagingROIType.ROI_TYPE_RECALL_REMINDERS]: {
    dailyVolume: 14,
    engagementRate: 27,
    scheduledRatio: 0.1,
    completedRatio: 0.07,
  },
  [MessagingROIType.ROI_TYPE_REACTIVATION_TEXT]: {
    dailyVolume: 11,
    engagementRate: 19,
    scheduledRatio: 0.06,
    completedRatio: 0.04,
  },
  [MessagingROIType.ROI_TYPE_RESCHEDULE_TEXT]: {
    dailyVolume: 7,
    engagementRate: 46,
    scheduledRatio: 0.31,
    completedRatio: 0.22,
  },
};

// Index = dayjs().day(), so Sunday first.
const WEEKDAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAY_WEIGHTS = [0.1, 1.1, 1.15, 1.1, 1.05, 0.95, 0.3];

// Relative message volume by hour of day (0-23), peaking during office hours.
const HOUR_WEIGHTS = [
  0, 0, 0, 0, 0, 0, 0.01, 0.03, 0.09, 0.12, 0.11, 0.09, 0.07, 0.08, 0.1, 0.1, 0.08, 0.06, 0.03, 0.02, 0.01, 0, 0, 0,
];

const DEFAULT_RANGE_DAYS = 30;
const MAX_RANGE_DAYS = 120;

// Deterministic jitter in [0, 1) so the demo data is stable between renders and refetches.
const seededRandom = (seed: string) => {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return ((hash >>> 0) % 10000) / 10000;
};

const EXISTING_PATIENT_SHARE = 0.85;

const getSeededContactType = (seed: string) =>
  seededRandom(`${seed}-contact`) < EXISTING_PATIENT_SHARE
    ? MessageContactType.MESSAGES_EXISTING_PATIENTS
    : MessageContactType.MESSAGES_UNKNOWN_PATIENTS;

// Counts the messages in a group whose seeded contact type passes the filter, matching the table rows.
const countMatchingContacts = (
  roiType: MessagingROIType,
  dateKey: string,
  engaged: boolean,
  count: number,
  contactType?: MessageContactType[]
) => {
  if (!contactType?.length) return count;
  let matching = 0;
  for (let i = 0; i < count; i++) {
    if (contactType.includes(getSeededContactType(`${roiType}-${dateKey}-${engaged ? 'e' : 'n'}-${i}`))) matching++;
  }
  return matching;
};

interface DemoPoint {
  name: string;
  nonEngagementCount: number;
  engagementCount: number;
}

const getDemoRange = (startDate?: string, endDate?: string) => {
  const end = endDate && dayjs(endDate).isValid() ? dayjs(endDate).startOf('day') : dayjs().subtract(1, 'day');
  const requestedStart = startDate && dayjs(startDate).isValid() ? dayjs(startDate).startOf('day') : undefined;
  const earliestStart = end.subtract(MAX_RANGE_DAYS - 1, 'day');
  const defaultStart = end.subtract(DEFAULT_RANGE_DAYS - 1, 'day');

  let start = requestedStart ?? defaultStart;
  if (start.isBefore(earliestStart)) start = earliestStart;
  if (start.isAfter(end)) start = end;

  return { start, end };
};

const buildDailyPoints = (
  roiType: MessagingROIType,
  profile: RoiDemoProfile,
  startDate?: string,
  endDate?: string,
  contactType?: MessageContactType[]
): DemoPoint[] => {
  const { start, end } = getDemoRange(startDate, endDate);
  const days = end.diff(start, 'day') + 1;

  return Array.from({ length: days }, (_, idx) => {
    const date = start.add(idx, 'day');
    const dateKey = date.format('YYYY-MM-DD');
    const volumeJitter = 0.75 + seededRandom(`${roiType}-${dateKey}-volume`) * 0.5;
    const engagementJitter = 0.8 + seededRandom(`${roiType}-${dateKey}-engagement`) * 0.4;

    const total = Math.round(profile.dailyVolume * (WEEKDAY_WEIGHTS[date.day()] ?? 1) * volumeJitter);
    const engaged = Math.min(total, Math.round(total * (profile.engagementRate / 100) * engagementJitter));

    return {
      name: dateKey,
      nonEngagementCount: countMatchingContacts(roiType, dateKey, false, total - engaged, contactType),
      engagementCount: countMatchingContacts(roiType, dateKey, true, engaged, contactType),
    };
  });
};

const toDayOfWeekPoints = (dailyPoints: DemoPoint[]): DemoPoint[] => {
  const byWeekday = WEEKDAY_NAMES.map((name) => ({ name, nonEngagementCount: 0, engagementCount: 0 }));
  dailyPoints.forEach((point) => {
    const bucket = byWeekday[dayjs(point.name).day()];
    if (!bucket) return;
    bucket.nonEngagementCount += point.nonEngagementCount;
    bucket.engagementCount += point.engagementCount;
  });
  return byWeekday;
};

const toHourlyPoints = (dailyPoints: DemoPoint[]): DemoPoint[] => {
  const totalEngaged = dailyPoints.reduce((sum, point) => sum + point.engagementCount, 0);
  const totalNonEngaged = dailyPoints.reduce((sum, point) => sum + point.nonEngagementCount, 0);

  const points = HOUR_WEIGHTS.map((weight, hour) => ({
    name: String(hour),
    engagementCount: Math.round(totalEngaged * weight),
    nonEngagementCount: Math.round(totalNonEngaged * weight),
  }));

  // Rounding can drift the totals; park the difference on the peak hour so hourly sums match the aggregates.
  const peak = points[HOUR_WEIGHTS.indexOf(Math.max(...HOUR_WEIGHTS))];
  if (peak) {
    peak.engagementCount += totalEngaged - points.reduce((sum, point) => sum + point.engagementCount, 0);
    peak.nonEngagementCount += totalNonEngaged - points.reduce((sum, point) => sum + point.nonEngagementCount, 0);
  }

  return points;
};

export interface MessagingRoiDemoRequest {
  startDate?: string;
  endDate?: string;
  filterType?: TimeFilterType;
  contactType?: MessageContactType[];
}

export interface MessagingRoiDemoData {
  aggregates: ROIMessageAggregatesResponse;
  apptAggregates: ROIMessagingApptAggregatesResponse;
  chart: ROIMessageDataResponse;
}

/**
 * Builds a consistent set of demo data (KPI aggregates, appointment aggregates and chart series)
 * for a messaging ROI type. All three are derived from the same daily series, so the KPI cards
 * always agree with the chart.
 */
export const getMessagingRoiDemoData = (
  roiType: MessagingROIType,
  { startDate, endDate, filterType = TimeFilterType.DAILY, contactType }: MessagingRoiDemoRequest = {}
): MessagingRoiDemoData | null => {
  const profile = ROI_DEMO_PROFILES[roiType];
  if (!profile) return null;

  const dailyPoints = buildDailyPoints(roiType, profile, startDate, endDate, contactType);

  const totalEngaged = dailyPoints.reduce((sum, point) => sum + point.engagementCount, 0);
  const totalMessages = dailyPoints.reduce((sum, point) => sum + point.engagementCount + point.nonEngagementCount, 0);
  const appointmentsScheduled = Math.round(totalMessages * profile.scheduledRatio);
  const appointmentsCompleted = Math.min(appointmentsScheduled, Math.round(totalMessages * profile.completedRatio));
  const safeTotal = totalMessages || 1;

  let chartPoints = dailyPoints;
  if (filterType === TimeFilterType.DAY_OF_WEEK) chartPoints = toDayOfWeekPoints(dailyPoints);
  if (filterType === TimeFilterType.HOURLY) chartPoints = toHourlyPoints(dailyPoints);

  const data: ROIMessageDataPoint[] = chartPoints.map(({ name, engagementCount, nonEngagementCount }) => ({
    name,
    engagementCount,
    nonEngagementCount,
  }));

  return {
    aggregates: {
      totalMessages,
      engagementRate: Math.round((totalEngaged / safeTotal) * 100),
    },
    apptAggregates: {
      appointmentsScheduled,
      appointmentsCompleted,
      appointmentsCompletedRate: Math.round((appointmentsCompleted / safeTotal) * 100),
      avgLocMsgLast30Days: Math.round((totalMessages / dailyPoints.length) * 30),
      avgApptsLast30Days: Math.round((appointmentsScheduled / dailyPoints.length) * 30),
    },
    chart: { data },
  };
};

export interface MessagingDemoLocation {
  id: string;
  name: string;
}

const DEFAULT_TABLE_PAGE_SIZE = 25;
const FIRST_NAMES = [
  'Olivia',
  'Liam',
  'Emma',
  'Noah',
  'Ava',
  'Elijah',
  'Sophia',
  'Lucas',
  'Mia',
  'Mason',
  'Amelia',
  'Ethan',
];
const LAST_NAMES = [
  'Johnson',
  'Smith',
  'Williams',
  'Brown',
  'Garcia',
  'Miller',
  'Davis',
  'Martinez',
  'Lopez',
  'Wilson',
];

const pickFrom = <T>(items: T[], random: number) => items[Math.floor(random * items.length)] as T;

const buildTableRows = (request: MessagingRequest, locations: MessagingDemoLocation[]): MessagingTableViewData[] => {
  const requestedTypes = request.roiType?.length ? request.roiType : DEMO_ROI_TYPES;
  const roiTypes = requestedTypes.filter(isMessagingRoiDemoType);
  const isOutboundAllowed =
    !request.smsDirection ||
    request.smsDirection === Direction.DIRECTION_UNSPECIFIED ||
    request.smsDirection === Direction.DIRECTION_OUTBOUND;
  if (!roiTypes.length || !isOutboundAllowed) return [];

  const includeEngaged = request.engaged !== EngagementFilterType.NOT_ENGAGED;
  const includeNotEngaged = request.engaged !== EngagementFilterType.ENGAGED;

  const rows: (MessagingTableViewData & { sortKey: string })[] = [];

  roiTypes.forEach((roiType) => {
    const profile = ROI_DEMO_PROFILES[roiType];
    if (!profile) return;

    // Rows come from the same daily series as the chart, so a bar's count matches its table rows.
    buildDailyPoints(roiType, profile, request.startDate, request.endDate).forEach((point) => {
      const groups = [
        { engaged: true, count: includeEngaged ? point.engagementCount : 0 },
        { engaged: false, count: includeNotEngaged ? point.nonEngagementCount : 0 },
      ];

      groups.forEach(({ engaged, count }) => {
        for (let i = 0; i < count; i++) {
          const seed = `${roiType}-${point.name}-${engaged ? 'e' : 'n'}-${i}`;
          const contactType = getSeededContactType(seed);
          if (request.contactType?.length && !request.contactType.includes(contactType)) continue;

          const locationIndex = Math.floor(seededRandom(`${seed}-location`) * Math.max(locations.length, 1));
          const location = locations[locationIndex];
          const messageDate = dayjs(point.name)
            .hour(8 + Math.floor(seededRandom(`${seed}-hour`) * 10))
            .minute(Math.floor(seededRandom(`${seed}-minute`) * 60))
            .second(Math.floor(seededRandom(`${seed}-second`) * 60))
            .format('YYYY-MM-DDTHH:mm:ss');
          const isExisting = contactType === MessageContactType.MESSAGES_EXISTING_PATIENTS;
          const phoneSuffix = String(Math.floor(seededRandom(`${seed}-phone`) * 10000)).padStart(4, '0');

          rows.push({
            sortKey: `${messageDate}-${seed}`,
            messageId: `demo-message-${seed}`,
            contactName: isExisting
              ? `${pickFrom(FIRST_NAMES, seededRandom(`${seed}-first`))} ${pickFrom(
                  LAST_NAMES,
                  seededRandom(`${seed}-last`)
                )}`
              : '',
            contactNumber: `801555${phoneSuffix}`,
            locationId: location?.id ?? '',
            locationName: location?.name ?? '',
            officeNumber: `80155501${String(locationIndex).padStart(2, '0')}`,
            phoneNumberName: 'Main Line',
            messageType: roiType,
            contactType,
            engaged: engaged ? EngagementFilterType.ENGAGED : EngagementFilterType.NOT_ENGAGED,
            messageDate,
            smsDirection: Direction.DIRECTION_OUTBOUND,
          });
        }
      });
    });
  });

  // Newest first, like the real table.
  return rows.sort((a, b) => b.sortKey.localeCompare(a.sortKey)).map(({ sortKey: _sortKey, ...row }) => row);
};

/**
 * Builds one page of demo rows for the messaging table view. Honours the request's ROI type,
 * engagement, direction and contact type filters and uses an offset as the page token.
 */
export const getMessagingTableDemoData = (
  request: MessagingRequest,
  locations: MessagingDemoLocation[]
): MessagingTableViewResponse => {
  const rows = buildTableRows(request, locations);
  const pageSize = request.pageSize || DEFAULT_TABLE_PAGE_SIZE;
  const offset = Number(request.pageToken) || 0;
  const nextOffset = offset + pageSize;

  return {
    data: rows.slice(offset, nextOffset),
    nextPageToken: nextOffset < rows.length ? String(nextOffset) : undefined,
  };
};
